import { useState } from "react";

import editIcon from "assets/images/edit-green-pencil.svg";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { InfoTooltip } from "components/ui/InfoTooltip";
import { Notice, NoticeText } from "components/ui/Notice";
import { useMaternityHospitals } from "features/pregnancy/hooks/useMaternityHospitals";
import { useMncmsInboundMessages } from "features/pregnancy/hooks/useMncmsInboundMessages";
import { usePregnancyGeneralViewState } from "features/pregnancy/hooks/usePregnancyGeneralViewState";
import { useReopenPregnancy } from "features/pregnancy/hooks/useReopenPregnancy";
import {
  describeFoetus,
  findEndedFoetuses,
  findLiveFoetuses,
} from "features/pregnancy/utils/endedFoetuses";
import {
  describeFoetusCount,
  findHospitalFoetusCountMismatch,
} from "features/pregnancy/utils/hospitalFoetusCount";
import { getEddMethodLabel } from "features/pregnancy/utils/normaliseEddMethod";
import {
  FAMILY_HISTORY_CHECK_INFO,
  FAMILY_HISTORY_CHECK_LABEL,
  patientCheckLabel,
} from "features/pregnancy/utils/patientCheckAnswers";
import {
  describePrivateRooms,
  findPrivateRoomsForPregnancy,
} from "features/pregnancy/utils/privateRooms";
import { formatDate } from "utils/dateFormatter.js";

import { CompletePregnancyDialog } from "../CompletePregnancyDialog/CompletePregnancyDialog.jsx";
import { EditPregnancyForm } from "../EditPregnancyForm/EditPregnancyForm.jsx";
import { EndFoetusDialog } from "../EndFoetusDialog/EndFoetusDialog.jsx";
import style from "./PregnancyGeneralView.module.css";

const AWAITING_HOSPITAL_MESSAGE = "Awaiting the hospital's first message";

export const PregnancyGeneralView = ({ pregnancy, patientId, tenantVal }) => {
  const {
    isModalOpen,
    openModal,
    closeModal,
    firstConsultationDate,
    doctorName,
    anteNatalConsultations,
  } = usePregnancyGeneralViewState(pregnancy);

  const { data: hospitalOptions } = useMaternityHospitals();

  /*
   * PV1.9 belongs to the pregnancy, not to a visit (KIN-2034). The hospital names the obstetrician's
   * private rooms once, on its messages about this pregnancy, and every GP message reflects the same
   * value back — so it read as a measurement taken at the visit while it sat in Maternal Exam.
   *
   * Read out of the pregnancy's inbound messages, which already carry the raw HL7: the endpoint that
   * returns the parsed value is keyed on a consultation, and this panel has none.
   */
  const { data: inboundMessages } = useMncmsInboundMessages(pregnancy.id);
  const privateRooms = describePrivateRooms(
    findPrivateRoomsForPregnancy(inboundMessages)
  );

  /*
   * The same messages answer a second question (KIN-2062). The backend raises our foetus count to
   * the hospital's when the hospital reports more, but a lower count it leaves alone and flags, so
   * the only place the difference exists is on the message — and nothing read it. It belongs here
   * rather than in the message log because this is the panel that states the count, and the GP is
   * reading the hospital's word against our own.
   */
  const foetusCountMismatch = findHospitalFoetusCountMismatch(
    inboundMessages,
    pregnancy.numberOfFoetuses
  );

  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
  const reopenPregnancy = useReopenPregnancy({
    tenantVal,
    pregnancyId: pregnancy.id,
  });

  const isCompleted = !!pregnancy.completed;

  /*
   * The remedy for the notice above (KIN-2066). A lower count from the hospital almost always
   * means a foetus existed and did not continue, so the GP records an outcome against that baby —
   * nothing is deleted, and the backend lowers the declared count to the remaining live foetuses
   * for us.
   *
   * Offered on the pregnancy as well as on the notice, so it is not reachable only from a message,
   * and here in General Information rather than in Edit pregnancy: Edit is a save-able form of the
   * pregnancy's own fields, where this would look like one more editable value, while this panel is
   * already where the count is stated and where the pregnancy's other state changes live.
   *
   * Hidden on the last remaining foetus, because a pregnancy whose only foetus did not continue is
   * a completed pregnancy and that is what Complete pregnancy records — the backend refuses it with
   * a 409 saying exactly that, which the dialog shows if it is reached another way.
   */
  const [isEndFoetusDialogOpen, setIsEndFoetusDialogOpen] = useState(false);
  const liveFoetuses = findLiveFoetuses(pregnancy);
  const endedFoetuses = findEndedFoetuses(anteNatalConsultations);
  const canRecordNotContinuing = !isCompleted && liveFoetuses.length > 1;

  /*
   * A dash said "nobody filled this in", which was true while the GP was asked for it and is wrong
   * now that nobody is (KIN-2060). The hospital is set from MSH.4 of its own first message, so an
   * empty one means the hospital has not written yet — which is also why the pregnancy cannot be
   * sent yet, and saying so here is what makes that gate explicable rather than arbitrary.
   */
  const hospitalLabel = pregnancy.receivingFacilityCode
    ? (hospitalOptions?.find(
        (hospital) => hospital.value === pregnancy.receivingFacilityCode
      )?.label ?? pregnancy.receivingFacilityCode)
    : AWAITING_HOSPITAL_MESSAGE;

  return (
    <div className={style.pregnancyGeneralInformationView}>
      <div className={style.generalInfoTitleWrapper}>
        <h1 className={style.generalInfoTitle}>General Information</h1>
        <div className={style.headerActions}>
          {/* Editing a completed pregnancy is still allowed — a wrong gravida is worth
              correcting after the fact — but it must not be the way one gets ended. */}
          <button className={style.editButton} onClick={openModal}>
            Edit
            <img src={editIcon} alt="edit pregnancy" />
          </button>
          {!isCompleted && (
            <Button
              variant="primary"
              type="button"
              className={style.completeButton}
              onClick={() => setIsCompleteDialogOpen(true)}
            >
              Complete pregnancy
            </Button>
          )}
        </div>
      </div>

      {/* The completed state is shown rather than merely acted on: a pregnancy that has quietly
          stopped offering its actions reads as broken, where one that says it ended, when, and
          why reads as finished. Reopen sits here too — visible, because a completion on the
          wrong record has to be undoable, and unobtrusive, because it is for mistakes. */}
      {!!isCompleted && (
        <div className={style.completedBanner} role="status">
          <div>
            <p className={style.completedTitle}>
              Pregnancy completed
              {pregnancy.completedAt
                ? ` on ${formatDate(pregnancy.completedAt)}`
                : ""}
            </p>
            {!!pregnancy.completionReason && (
              <p className={style.completedReason}>
                {pregnancy.completionReason}
              </p>
            )}
          </div>
          <Button
            variant="text"
            size="small"
            type="button"
            disabled={reopenPregnancy.isPending}
            onClick={() => reopenPregnancy.mutate()}
          >
            {reopenPregnancy.isPending ? "Reopening…" : "Reopen"}
          </Button>
        </div>
      )}
      {/* Informational, not blocking: keeping our count may well be the right call, so nothing is
          withheld or disabled by this. The action offered is to record an outcome, never to delete
          a foetus — a foetus can carry recorded observations, and those are kept (KIN-2066). */}
      {!!foetusCountMismatch && (
        <div className={style.foetusCountNotice}>
          <Notice
            tone="info"
            testId="foetus-count-mismatch"
            action={
              canRecordNotContinuing ? (
                <Button
                  variant="outline"
                  size="small"
                  type="button"
                  className={style.endFoetusNoticeButton}
                  onClick={() => setIsEndFoetusDialogOpen(true)}
                >
                  Record a foetus as not continuing
                </Button>
              ) : null
            }
          >
            <NoticeText isLead>
              The maternity hospital reports a different number of foetuses
            </NoticeText>
            {/* "This record shows", not "this pregnancy holds": a GP reads "holds" as a database
                verb and has to stop and work out whether it means our record or the woman. And the
                reassurance is its own sentence, so it still lands when the reason behind it is
                skimmed past. */}
            <NoticeText>
              {`Its most recent message reports ${describeFoetusCount(
                foetusCountMismatch.reported
              )}; this record shows ${describeFoetusCount(
                foetusCountMismatch.held
              )}. Nothing has been changed.`}
            </NoticeText>
            <NoticeText>
              A foetus may already carry recorded observations, so one is never
              removed on a message&apos;s word.
            </NoticeText>
          </Notice>
        </div>
      )}
      <div className={style.generalInfoMainSection}>
        <div>
          <h3>First consultation</h3>
          <p>{firstConsultationDate}</p>
        </div>
        <div>
          <h3>Doctor</h3>
          <p>{doctorName}</p>
        </div>
        <div>
          <h3>Gravida</h3>
          <p>{pregnancy.gravida}</p>
        </div>
        <div>
          <h3>Para</h3>
          <p>{pregnancy.para}</p>
        </div>
        <div>
          <h3>Last Menstrual Period</h3>
          <p>{formatDate(pregnancy.lastMenstrualPeriod)}</p>
        </div>
        <div>
          <h3>Estimated Due Date</h3>
          <p>{formatDate(pregnancy.estimatedDueDate)}</p>
        </div>
        <div>
          <h3>EDD Method</h3>
          <p>{getEddMethodLabel(pregnancy.eddMethod) || "-"}</p>
        </div>
        <div>
          <h3>Number of foetuses</h3>
          <p>{pregnancy.numberOfFoetuses ?? "-"}</p>
          {/* Beside the number it changes, rather than up with Edit and Complete pregnancy: this
              is not an action on the pregnancy as a whole, and read here it is obvious which
              value moves. */}
          {!!canRecordNotContinuing && (
            <Button
              variant="text"
              size="small"
              type="button"
              className={style.endFoetusLink}
              onClick={() => setIsEndFoetusDialogOpen(true)}
            >
              Record a foetus as not continuing
            </Button>
          )}
        </div>
        {/* Shown rather than hidden: the baby and its outcome are part of this pregnancy's story,
            and a record that silently went from two foetuses to one reads as a lost entry. The
            outcome is free text a user typed, so it is rendered as text (KIN-2043). */}
        {endedFoetuses.length > 0 && (
          <div>
            {/* "Foetus outcomes" rather than "Not continuing", so the heading does not repeat the
                words each line already says. */}
            <h3>Foetus outcomes</h3>
            {endedFoetuses.map((foetus) => (
              <p key={foetus.id} data-testid="ended-foetus">
                {describeFoetus(foetus)}, not continuing
                {foetus.outcome ? `: ${foetus.outcome}` : ""}
              </p>
            ))}
          </div>
        )}
        {/* No "from the hospital's message of …" on either of these two, unlike Private rooms.
            Private rooms is parsed out of a message in front of us, so its provenance is known;
            the hospital and the foetus count are plain columns that the backend may have filled
            from a message or a GP may have typed in Edit, and the DTO carries no source. Labelling
            a typed value as the hospital's word would be worse than labelling nothing. */}
        <div>
          <h3>Hospital</h3>
          <p data-testid="receiving-hospital">{hospitalLabel}</p>
        </div>
        {/* Nothing at all for a public patient: the hospital named no rooms, none will be sent,
            and an empty row or a dash would claim there is a value we failed to find. */}
        {!!privateRooms && (
          <div>
            <h3>Private rooms</h3>
            <p data-testid="private-rooms">{privateRooms}</p>
          </div>
        )}
        {/* The four patient checks read as Yes / No / Not asked, in the same tiles as every other
            field (KIN-2056). They were disabled tick boxes, which could only show two states and
            showed the unanswered ones as unticked — indistinguishable from a No. None of the four
            is an MN-CMS field, so nothing on the wire reads them. */}
        <div>
          <h3>Clinic HCG positive</h3>
          <p data-testid="check-clinic-hcg-positive">
            {patientCheckLabel(pregnancy.confirmHcgPositive)}
          </p>
        </div>
        <div>
          <h3>Smoker</h3>
          <p data-testid="check-smoker">
            {patientCheckLabel(pregnancy.smoker)}
          </p>
        </div>
        {/* The tooltip is repeated here rather than left to the form, because this is where the
            answer is read without the question in front of you: the shortened label no longer
            names the abnormality, and a bare "Yes" is the easiest of the four to read backwards. */}
        <div>
          <h3 className={style.checkHeading}>
            {FAMILY_HISTORY_CHECK_LABEL}
            <InfoTooltip text={FAMILY_HISTORY_CHECK_INFO} />
          </h3>
          <p data-testid="check-family-history-ruled-out">
            {patientCheckLabel(pregnancy.familyHistoryCongentialAbnormality)}
          </p>
        </div>
        <div>
          <h3>Has had chicken pox</h3>
          <p data-testid="check-has-had-chicken-pox">
            {patientCheckLabel(pregnancy.hasChickenPox)}
          </p>
        </div>
      </div>
      <EndFoetusDialog
        open={isEndFoetusDialogOpen}
        onClose={() => setIsEndFoetusDialogOpen(false)}
        tenantVal={tenantVal}
        pregnancyId={pregnancy.id}
        liveFoetuses={liveFoetuses}
      />

      <CompletePregnancyDialog
        open={isCompleteDialogOpen}
        onClose={() => setIsCompleteDialogOpen(false)}
        tenantVal={tenantVal}
        pregnancyId={pregnancy.id}
      />

      <Dialog
        open={isModalOpen}
        title="Edit pregnancy"
        onClose={closeModal}
        width={1024}
      >
        <EditPregnancyForm
          currentPatientId={patientId}
          closeModal={closeModal}
          tenantVal={tenantVal}
          pregnancy={pregnancy}
        />
      </Dialog>
    </div>
  );
};
